import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { formatCurrency } from "../../utils/billing-utils";
import type { InternalReferral, ReferralTier } from "../types/commissions.types";
import { CommissionTableEmptyState } from "./commission-table-empty-state";

interface InternalReferralsTableProps {
  referrals: InternalReferral[];
  quarterLabel: string;
  onRowClick: (referral: InternalReferral) => void;
  referralIdToEstimatedCommission?: Record<string, number>;
}

const TIER_LABELS: Record<ReferralTier, string> = {
  existing: "Existing (6%)",
  new: "New (20%)",
  after_one_year: "After 1 yr (10%)",
};

const InternalReferralsTable: React.FC<InternalReferralsTableProps> = ({
  referrals,
  quarterLabel,
  onRowClick,
  referralIdToEstimatedCommission,
}) => {
  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Referring employee</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Client</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Start date</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Tier</TableHead>
            <TableHead className={portalDs.tableHeadCell}>
              Est. Commission ({quarterLabel})
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {referrals.length === 0 ? (
            <CommissionTableEmptyState
              colSpan={5}
              title="No internal referrals yet"
              description="Add a referral to track company-wide commission for referred clients."
            />
          ) : (
            referrals.map((ref) => (
              <TableRow
                key={ref.id}
                className={cn(portalDs.tableBodyRow, portalDs.tableBodyRowInteractive)}
                onClick={() => onRowClick(ref)}
              >
                <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                  {ref.referringEmployeeName}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {ref.clientName}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {ref.startDate}
                </TableCell>
                <TableCell className={portalDs.tableCell}>{TIER_LABELS[ref.tier]}</TableCell>
                <TableCell className={portalDs.tableCell}>
                  {referralIdToEstimatedCommission?.[ref.id] != null
                    ? formatCurrency(referralIdToEstimatedCommission[ref.id])
                    : ref.estimatedPayoutForQuarter != null
                      ? formatCurrency(ref.estimatedPayoutForQuarter)
                      : "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default InternalReferralsTable;
